import { Trans } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import type { ModelsPublicReturnLineItem } from '@itsrever/dashboard-api'
import {
  WarningCircleIcon,
  PriceTagIcon,
  CollectedIcon,
  CheckIcon,
  ForbiddenIcon
} from '@itsrever/design-system'
import { ShippingStatuses, ShippingStatusesOld } from '@/utils'
import { ListDate } from '@/ui/shared/list-tabs'
import { ItemTranslatedReturnReason } from './item-translated-return-reason'

const { Badge, createColumnHelper } = Base

const columnHelper = createColumnHelper<ModelsPublicReturnLineItem>()

export const columns = [
  columnHelper.accessor('return_process.started_at.seconds', {
    header: () => <Trans i18nKey="items.return_date" />,
    cell: (info) => {
      const date = info.getValue()

      if (date) {
        return (
          <span className="whitespace-nowrap font-sans">
            <ListDate date={new Date(date * 1000)} />
          </span>
        )
      }

      return null
    },
    meta: {
      cellClassName: 'py-1 w-0'
    },
    sortDescFirst: true
  }),
  columnHelper.accessor('return_process.customer_printed_order_id', {
    header: () => <Trans i18nKey="items.order" />,
    meta: {
      cellClassName: 'py-1 w-0'
    }
  }),
  columnHelper.accessor('return_process.customer.first_name', {
    header: () => <Trans i18nKey="items.customer" />,
    cell: (info) => (
      <span className="block max-w-28 truncate font-sans">
        {info.getValue()}{' '}
        {info.row.original.return_process?.customer?.last_name}
      </span>
    ),
    meta: {
      cellClassName: 'py-1 w-0'
    }
  }),
  columnHelper.accessor('quantity', {
    header: () => <Trans i18nKey="items.quantity" />,
    cell: (info) => info.getValue()?.toLocaleString(),
    meta: {
      cellClassName: 'text-center py-1 w-0',
      headClassName: 'text-center'
    }
  }),
  columnHelper.accessor('name', {
    header: () => <Trans i18nKey="items.product" />,
    cell: (info) => (
      <figure className="flex items-center gap-3 truncate font-sans text-sm font-normal">
        <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-solid border-neutral-300">
          {info.row.original.product_image_url ? (
            <img
              loading="lazy"
              width={40}
              height={40}
              className="size-10 rounded-md object-cover"
              src={info.row.original.product_image_url}
              alt={info.row.original.name}
            />
          ) : (
            <svg
              stroke="currentColor"
              fill="none"
              strokeWidth="0"
              viewBox="0 0 15 15"
              height="16"
              width="16"
              xmlns="http://www.w3.org/2000/svg"
              className="text-neutral-500"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.5 1H12.5C13.3284 1 14 1.67157 14 2.5V12.5C14 13.3284 13.3284 14 12.5 14H2.5C1.67157 14 1 13.3284 1 12.5V2.5C1 1.67157 1.67157 1 2.5 1ZM2.5 2C2.22386 2 2 2.22386 2 2.5V8.3636L3.6818 6.6818C3.76809 6.59551 3.88572 6.54797 4.00774 6.55007C4.12975 6.55216 4.24568 6.60372 4.32895 6.69293L7.87355 10.4901L10.6818 7.6818C10.8575 7.50607 11.1425 7.50607 11.3182 7.6818L13 9.3636V2.5C13 2.22386 12.7761 2 12.5 2H2.5ZM2 12.5V9.6364L3.98887 7.64753L7.5311 11.4421L8.94113 13H2.5C2.22386 13 2 12.7761 2 12.5ZM12.5 13H10.155L8.48336 11.153L11 8.6364L13 10.6364V12.5C13 12.7761 12.7761 13 12.5 13ZM6.64922 5.5C6.64922 5.03013 7.03013 4.64922 7.5 4.64922C7.96987 4.64922 8.35078 5.03013 8.35078 5.5C8.35078 5.96987 7.96987 6.35078 7.5 6.35078C7.03013 6.35078 6.64922 5.96987 6.64922 5.5ZM7.5 3.74922C6.53307 3.74922 5.74922 4.53307 5.74922 5.5C5.74922 6.46693 6.53307 7.25078 7.5 7.25078C8.46693 7.25078 9.25078 6.46693 9.25078 5.5C9.25078 4.53307 8.46693 3.74922 7.5 3.74922Z"
                fill="currentColor"
              ></path>
            </svg>
          )}
        </span>
        <figcaption className="line-clamp-2 w-36 min-w-0 whitespace-normal leading-6">
          {info.getValue()}
        </figcaption>
      </figure>
    ),
    meta: {
      cellClassName: 'py-1 w-0'
    }
  }),
  columnHelper.accessor('sku', {
    header: () => <Trans i18nKey="items.sku" />,
    cell: (info) => {
      const sku = info.getValue()

      if (sku) {
        return (
          <div className="w-32 whitespace-normal break-words font-sans">
            {sku}
          </div>
        )
      }

      return null
    },
    meta: {
      cellClassName: 'py-1 w-0'
    }
  }),
  columnHelper.accessor('product_return_reason', {
    header: () => <Trans i18nKey="items.return_reason" />,
    cell: (info) => {
      const reason = info.getValue()

      if (reason) {
        return <ItemTranslatedReturnReason reason={reason} />
      }

      return null
    },
    meta: {
      cellClassName: 'py-1 w-0'
    }
  }),
  columnHelper.accessor('return_process.last_known_shipping_status', {
    header: () => <Trans i18nKey="items.shipping" />,
    cell: (info) => (
      <ShippingStatusCell
        shippingStatus={info.getValue()}
        provider={info.row.original.return_process?.user_selected_provider}
      />
    ),
    meta: {
      cellClassName: 'py-1 w-0'
    }
  }),
  columnHelper.accessor('reviews', {
    header: () => <Trans i18nKey="items.review" />,
    cell: (info) => {
      const reviews = info.getValue()

      if (reviews && reviews.length > 0) {
        const counts = reviews?.reduce(
          (accMap: { [key: string]: number }, review) => {
            const key = review.status ?? ''
            accMap[key] = (accMap[key] || 0) + 1
            return accMap
          },
          {}
        )

        const maxReviews = reviews.length
        const totalApproved = counts['APPROVED'] ?? 0
        const totalDeclined = counts['DECLINED'] ?? 0
        const totalMissing = counts['MISSING'] ?? 0

        if (totalDeclined + totalMissing === maxReviews) {
          return (
            <Badge tone="danger">
              <Trans i18nKey="return_status.declined" />
            </Badge>
          )
        }

        if (totalApproved === maxReviews) {
          return (
            <Badge tone="success">
              <Trans i18nKey="return_status.approved" />
            </Badge>
          )
        }

        return (
          <Badge tone="warning">
            <Trans i18nKey="return_status.partially_declined" />
          </Badge>
        )
      }

      return (
        <Badge>
          <Trans i18nKey={`items.review_pending`} />
        </Badge>
      )
    },
    meta: {
      cellClassName: 'py-1 w-0'
    }
  })
]

// Mirrors logisticsStatusOf in processes-api: the provider is checked before the
// shipping status, so a return with no provider reports no shipping at all.
const NO_LOGISTICS_PROVIDER = 'NONE'

interface ShippingStatusCellProps {
  shippingStatus?: number
  provider?: string
}

export const ShippingStatusCell = ({
  shippingStatus,
  provider
}: ShippingStatusCellProps) => {
  if (provider === NO_LOGISTICS_PROVIDER) {
    return <span className="text-neutral-400">—</span>
  }

  switch (shippingStatus) {
    case ShippingStatusesOld.Created:
      return (
        <Badge tone="info" icon={<PriceTagIcon />}>
          <Trans i18nKey={`shipping_status.${ShippingStatuses.Created}`} />
        </Badge>
      )
    case ShippingStatusesOld.Collected:
      return (
        <Badge tone="info" icon={<CollectedIcon />}>
          <Trans i18nKey={`shipping_status.${ShippingStatuses.Collected}`} />
        </Badge>
      )
    case ShippingStatusesOld.InWarehouse:
      return (
        <Badge tone="success" icon={<CheckIcon />}>
          <Trans i18nKey={`shipping_status.${ShippingStatuses.InWarehouse}`} />
        </Badge>
      )
    case ShippingStatusesOld.Canceled:
      return (
        <Badge icon={<ForbiddenIcon />}>
          <Trans i18nKey={`shipping_status.${ShippingStatuses.Canceled}`} />
        </Badge>
      )
    case ShippingStatusesOld.Error:
      return (
        <Badge tone="danger" icon={<WarningCircleIcon />}>
          <Trans i18nKey={`shipping_status.${ShippingStatuses.Error}`} />
        </Badge>
      )
    default:
      return (
        <Badge tone="warning" icon={<WarningCircleIcon />}>
          <Trans
            i18nKey={`shipping_status.${ShippingStatuses.NoShippingStatus}`}
          />
        </Badge>
      )
  }
}
